import { createContext, useContext, useState, type ReactNode } from 'react';
import { useTournaments } from './useTournaments';
import { useSeries } from './useSeries';
import { useArchive } from './useArchive';
import { usePlayers } from './usePlayers';
import { useBookings } from './useBookings';
import { useActivities } from './useActivities';
import { useBookingSettings } from './useBookingSettings';
import { useAssets } from './useAssets';
import { useClubInfo } from './useClubInfo';
import { useInitialLoad } from './useInitialLoad';
import { useMemberBootstrap } from './useMemberBootstrap';
import { DEFAULT_CLUB_INFO } from './useClubInfo';
import { BOOKING_DEFAULT_CONFIG } from '../engine';
import type { Toast } from './toast';

/**
 * What a club slice reads while the store holds no data for the ACTIVE club.
 *
 * Module constants, so a masked render hands every consumer the same
 * references and no memo or effect re-fires because of the mask itself.
 */
const NO_ROWS: any[] = [];

export type { Toast, ToastAction } from './toast';

/**
 * Composes the eight persisted-state hooks (+ the toast system + the
 * banepad-locked-court atom) into a single store exposed via React context, and
 * owns the atomic cross-slice mount loader. The core of the `Padel24AppMain` →
 * `AppShell` shrink (see docs/history/DECOMPOSITION_PLAN.md §1-2): the god component now
 * reads everything from `useStore()` instead of calling the eight hooks itself.
 *
 * IMPORTANT (R1 / §13.20): the persisted slices keep their state + loaded-flag +
 * persist effect together inside their own hooks. The **atomic mount loader**
 * (useInitialLoad) now lives here too (PR-AF) and flushes Storage through those
 * hooks' setters/refs — keeping state + flag + persist + loader all in the state
 * layer (principle #1). It is async, so its setter-flushes + flag-flips happen
 * after the initial effect pass; the persist effects therefore skip on mount
 * (flags false) exactly as before. StrictMode stays OFF (R2). The live
 * in-monolith `Padel24Engine` is passed in as a prop because the loader's
 * migrations call `Padel24Engine.playerDB.base()` / `.settings.apply()` /
 * `.booking.DEFAULT_*`.
 */
function useStoreValue(Padel24Engine: any, activeClubId: string | null) {
  // Toast — owned here because useClubInfo/usePlayers take showToast.
  const [toast, setToast] = useState<Toast | null>(null);
  const showToast = (type: string, message: string, duration = 4000) => {
    const shown: Toast = { type, message };
    setToast(shown);
    // Clears THIS toast only. An unconditional clear let an earlier toast's
    // timer remove whichever toast was showing when it fired — including one
    // that offers an action and is meant to stay until it is used (#1305).
    setTimeout(() => setToast((curr) => (curr === shown ? null : curr)), duration);
  };

  // The eight persisted-state hooks. No key collisions across their return
  // objects (verified), so spreading them into one store is unambiguous.
  const tournaments = useTournaments({ setToast });
  const series = useSeries({ setToast });
  const archive = useArchive({ setToast });
  const players = usePlayers(showToast);
  const bookings = useBookings();
  const activities = useActivities();
  const bookingSettings = useBookingSettings();
  // Equipment beside the courts (docs/domains/ASSET_BOOKING.md): the registry
  // and the asset bookings, both server-authoritative on the http backend.
  const assetsSlice = useAssets();
  const clubInfo = useClubInfo(showToast);

  // BANEPAD: which court this device is locked to (kiosk). Loaded by the atomic
  // loader below via the exposed setter.
  const [banepadLockedCourt, setBanepadLockedCourt] = useState<number | null>(null);

  // Atomic cross-slice mount loader — reads every persisted key once and flushes
  // the results through these setters/refs, then flips each loaded-flag (R1).
  useInitialLoad({
    setTournament: tournaments.setTournament,
    setActiveSeries: series.setActiveSeries,
    setActivities: activities.setActivities,
    setAllPlayers: players.setAllPlayers,
    setArchive: archive.setArchive,
    setArchiveLoaded: archive.setArchiveLoaded,
    setBanepadLockedCourt,
    setBookingConfig: bookingSettings.setBookingConfig,
    setBookingHoursSaved: bookingSettings.setBookingHoursSaved,
    setBookingZones: bookingSettings.setBookingZones,
    setBookingGuestPrices: bookingSettings.setBookingGuestPrices,
    setBookings: bookings.setBookings,
    setAssets: assetsSlice.setAssets,
    setAssetBookings: assetsSlice.setAssetBookings,
    setEloAdjustments: players.setEloAdjustments,
    setPausedSeries: series.setPausedSeries,
    setPausedTournaments: tournaments.setPausedTournaments,
    setSingleMatches: players.setSingleMatches,
    setPlannedMatches: players.setPlannedMatches,
    activeSeriesLoaded: series.activeSeriesLoaded,
    activeTournLoaded: tournaments.activeTournLoaded,
    bookingsRef: bookings.bookingsRef,
    pausedLoaded: tournaments.pausedLoaded,
    pausedSeriesLoaded: series.pausedSeriesLoaded,
    Padel24Engine,
  });

  // THE MEMBER APP'S LOADER (#185). Mutually exclusive with the one above —
  // each checks `memberModeActive()` and only one of them does anything, so a
  // member never fires the club's `/kv` reads and a club user never fires the
  // member bootstrap. Its status is exposed so the shell can tell «loading»
  // from «this failed» instead of rendering an empty club.
  const memberBootstrap = useMemberBootstrap(
    {
      setAllPlayers: players.setAllPlayers,
      setBookings: bookings.setBookings,
      setActivities: activities.setActivities,
      setPlannedMatches: players.setPlannedMatches,
      setSingleMatches: players.setSingleMatches,
      setMemberHistory: archive.setMemberHistory,
      setMemberMe: archive.setMemberMe,
      setBookingConfig: bookingSettings.setBookingConfig,
      setBookingZones: bookingSettings.setBookingZones,
      setClubInfo: clubInfo.setClubInfo,
      setAssets: assetsSlice.setAssets,
      setAssetBookings: assetsSlice.setAssetBookings,
      bookingsRef: bookings.bookingsRef,
    },
    activeClubId,
  );

  // ── AN ATOMIC CLUB-DATA BOUNDARY, AT RENDER TIME (#848 review) ──────
  //
  // The player app stays mounted through a club switch, so the store must
  // never PUBLISH one club's data under another's selection — not for one
  // render. The bootstrap empties the slices in an effect, which is after the
  // render that first sees the new club; masking here is what makes the
  // boundary hold on that render too, for every consumer at once (everything
  // derived downstream — `effectivePlayerDB`, the member's lists, the centres —
  // is computed from these values).
  //
  // Masked until the active club's OWN answer has arrived, and whenever no club
  // is selected. The setters, refs and actions are untouched: the mask is what
  // the screens READ, and the club-specific screens are themselves blocked
  // while the club is not ready (`MobileApp`).
  const visible = memberBootstrap.clubDataVisible;

  return {
    toast,
    setToast,
    showToast,
    banepadLockedCourt,
    setBanepadLockedCourt,
    // Exposed so a screen can tell «still loading» from «this failed» (#185).
    // The member app has no second data source: if the bootstrap did not
    // arrive, an empty club on screen is a lie, not a slow start.
    memberBootstrap,
    ...tournaments,
    ...series,
    ...archive,
    ...players,
    ...bookings,
    ...activities,
    ...bookingSettings,
    ...assetsSlice,
    ...clubInfo,
    allPlayers: visible ? players.allPlayers : NO_ROWS,
    assets: visible ? assetsSlice.assets : NO_ROWS,
    assetBookings: visible ? assetsSlice.assetBookings : NO_ROWS,
    singleMatches: visible ? players.singleMatches : NO_ROWS,
    plannedMatches: visible ? players.plannedMatches : NO_ROWS,
    bookings: visible ? bookings.bookings : NO_ROWS,
    activities: visible ? activities.activities : NO_ROWS,
    memberHistory: visible ? archive.memberHistory : NO_ROWS,
    memberMe: visible ? archive.memberMe : null,
    bookingConfig: visible ? bookingSettings.bookingConfig : BOOKING_DEFAULT_CONFIG,
    bookingZones: visible ? bookingSettings.bookingZones : NO_ROWS,
    bookingGuestPrices: visible ? bookingSettings.bookingGuestPrices : null,
    clubInfo: visible ? clubInfo.clubInfo : DEFAULT_CLUB_INFO,
  };
}

export type Store = ReturnType<typeof useStoreValue>;

const StoreContext = createContext<Store | null>(null);

export function StoreProvider({
  Padel24Engine,
  activeClubId = null,
  children,
}: {
  /** The live in-monolith engine object (legacy/App.tsx) — used by the loader. */
  Padel24Engine: any;
  /**
   * The club a PLAYER has selected as their active context, or null.
   *
   * A prop, not a key. Changing it reloads the club's data IN PLACE, so the
   * player app above it keeps its navigation — see `useMemberBootstrap`. The
   * store is still keyed on identity, impersonation and the organization
   * scope, where a remount is exactly what is wanted.
   */
  activeClubId?: string | null;
  children: ReactNode;
}) {
  const value = useStoreValue(Padel24Engine, activeClubId);
  return <StoreContext.Provider value={value}>{children}</StoreContext.Provider>;
}

/** Access the composed store. Must be called inside a <StoreProvider>. */
export function useStore(): Store {
  const ctx = useContext(StoreContext);
  if (!ctx) throw new Error('useStore must be used within a <StoreProvider>');
  return ctx;
}
